import React from "react";
import { motion } from "framer-motion";
import { Scan, Carrot, Droplets, ChefHat } from "lucide-react";
import { IMAGES } from "@/data/content";
import { optimizeImg } from "@/lib/img";

const macros = [
  { label: "Protein", value: 142, max: 160, color: "#00E676" },
  { label: "Carbs", value: 210, max: 280, color: "#ffffff" },
  { label: "Fats", value: 58, max: 70, color: "#00C853" },
];

const tools = [
  { icon: Scan, label: "Barcode Scanning" },
  { icon: Carrot, label: "Huge Food Database" },
  { icon: Droplets, label: "Hydration Reminders" },
  { icon: ChefHat, label: "Personalized Recipes" },
];

export const Nutrition = () => {
  return (
    <section id="nutrition" data-testid="nutrition-section" className="relative py-24 md:py-32 bg-obsidian">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-vitality">Nutrition & Recipes</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight mt-4 leading-tight">
            Fuel Your Goals
          </h2>
          <p className="text-white/60 mt-4 text-lg leading-relaxed">
            Nutrition is half the battle — and AlphaPulse helps you win it. Log meals, track macros and access a huge
            food database with barcode scanning. Get personalized meal plans for vegan, keto, gluten-free and more,
            delivered to your inbox every month.
          </p>

          <div className="grid grid-cols-2 gap-3 mt-8">
            {tools.map((t, i) => (
              <div key={t.label} data-testid={`nutrition-tool-${i}`} className="flex items-center gap-3 bg-charcoal border border-white/10 rounded-xl p-4">
                <t.icon className="w-5 h-5 text-vitality shrink-0" />
                <span className="text-sm font-medium text-white/80">{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="rounded-3xl overflow-hidden border border-white/10">
            <img src={optimizeImg(IMAGES.nutrition, 900)} alt="Healthy meal bowl" loading="lazy" decoding="async" className="w-full h-72 object-cover" />
          </div>
          <div className="glass rounded-2xl p-6 mt-[-40px] mx-4 relative">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading text-lg uppercase tracking-tight">Today's Macros</span>
              <span className="text-vitality text-sm font-bold">1,840 kcal</span>
            </div>
            <div className="space-y-4">
              {macros.map((m) => (
                <div key={m.label}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-white/70">{m.label}</span>
                    <span className="text-white/50">{m.value} / {m.max} g</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(m.value / m.max) * 100}%`, background: m.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
